import { __awaiter, __decorate } from "tslib";
import { makeObservable, observable, runInAction } from "mobx";
import { onSnapshot } from "mobx-state-tree";
import { LearningLogDocument, PersonalDocument } from "../../models/document/document-types";
import { GroupActivityBroadcaster } from "../group-activity-broadcaster";
import { DrivingQuestionBoardActivityBroadcaster } from "../driving-question-board-activity-broadcaster";
import { DBLatestGroupIdListener } from "./db-latest-group-id-listener";
import { DBGroupsListener } from "./db-groups-listener";
import { DBGroupActivityListener } from "./db-group-activity-listener";
import { DBDrivingQuestionBoardActivityListener } from "./db-driving-question-board-activity-listener";
import { DBOtherDocumentsListener } from "./db-other-docs-listener";
import { DBProblemDocumentsListener } from "./db-problem-documents-listener";
import { DBPublicationsListener } from "./db-publications-listener";
import { DBSupportsListener } from "./db-supports-listener";
import { DBCommentsListener } from "./db-comments-listener";
import { DBBookmarksListener } from "./db-bookmarks-listener";
import { BaseListener } from "./base-listener";
import { DBDocumentsContentListener } from "./db-docs-content-listener";
import { DBStudentPersonalDocsListener } from "./db-student-personal-docs-listener";
import { DBExemplarsListener } from "./db-exemplars-listener";
export class DBListeners extends BaseListener {
    constructor(db) {
        super("DBListeners");
        Object.defineProperty(this, "isListening", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "db", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "latestGroupIdListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "groupsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "groupActivityListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "dqbActivityListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "problemDocumentsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "personalDocumentsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "learningLogsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "publicationListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "studentPersonalDocsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "supportsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "commentsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "bookmarksListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "documentsContentListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "exemplarsListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "groupActivityBroadcaster", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "dqbActivityBroadcaster", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // sync local support document properties to firebase (teachers only)
        // TODO: move this to client-side hook as was done with other document monitoring
        Object.defineProperty(this, "syncSupportDocumentProperties", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (document, dbType, path) => {
                const { user } = this.db.stores;
                const { key } = document;
                if (dbType === "firebase") {
                    const updatePath = path || this.db.firebase.getUserDocumentMetadataPath(user, key, document.uid);
                    const updateRef = this.db.firebase.ref(updatePath);
                    // synchronize document property changes to firebase
                    onSnapshot(document.properties, properties => updateRef.update({ properties }));
                }
                else if (dbType === "firestore") {
                    const docRef = this.db.firestore.getMulticlassSupportDocumentRef(key);
                    // synchronize document property changes to firestore
                    onSnapshot(document.properties, properties => docRef.update({ properties }));
                }
            }
        });
        makeObservable(this);
        this.db = db;
        this.latestGroupIdListener = new DBLatestGroupIdListener(db);
        this.groupsListener = new DBGroupsListener(db);
        this.groupActivityListener = new DBGroupActivityListener(db);
        this.dqbActivityListener = new DBDrivingQuestionBoardActivityListener(db);
        this.problemDocumentsListener = new DBProblemDocumentsListener(db);
        this.personalDocumentsListener = new DBOtherDocumentsListener(db, PersonalDocument);
        this.learningLogsListener = new DBOtherDocumentsListener(db, LearningLogDocument);
        this.publicationListener = new DBPublicationsListener(db);
        this.studentPersonalDocsListener = new DBStudentPersonalDocsListener(db, PersonalDocument);
        this.supportsListener = new DBSupportsListener(db);
        this.commentsListener = new DBCommentsListener(db);
        this.bookmarksListener = new DBBookmarksListener(db);
        this.documentsContentListener = new DBDocumentsContentListener(db);
        this.exemplarsListener = new DBExemplarsListener(db);
        this.groupActivityBroadcaster = new GroupActivityBroadcaster(db);
        this.dqbActivityBroadcaster = new DrivingQuestionBoardActivityBroadcaster(db);
    }
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            // listeners must start in this order so we know the latest group joined so we can autojoin groups if needed
            yield this.latestGroupIdListener.start();
            // start group and document listeners
            yield Promise.all([
                this.groupsListener.start(),
                this.problemDocumentsListener.start(),
                this.personalDocumentsListener.start(),
                this.learningLogsListener.start(),
                this.publicationListener.start(),
                this.studentPersonalDocsListener.start(),
                this.supportsListener.start()
            ]);
            // start group activity listener after groups listener so currentGroupId is set
            yield this.groupActivityListener.start();
            this.groupActivityBroadcaster.start();
            // class-wide presence for the Driving Question Board (independent of group membership)
            yield this.dqbActivityListener.start();
            this.dqbActivityBroadcaster.start();
            // start listeners that depend on documents
            yield Promise.all([
                this.commentsListener.start(),
                this.bookmarksListener.start(),
                this.documentsContentListener.start(),
                this.exemplarsListener.start()
            ]);
            runInAction(() => this.isListening = true);
        });
    }
    stop() {
        runInAction(() => this.isListening = false);
        this.documentsContentListener.stop();
        this.bookmarksListener.stop();
        this.commentsListener.stop();
        this.supportsListener.stop();
        this.publicationListener.stop();
        this.studentPersonalDocsListener.stop();
        this.learningLogsListener.stop();
        this.personalDocumentsListener.stop();
        this.problemDocumentsListener.stop();
        this.groupActivityBroadcaster.stop();
        this.groupActivityListener.stop();
        this.dqbActivityBroadcaster.stop();
        this.dqbActivityListener.stop();
        this.groupsListener.stop();
        this.latestGroupIdListener.stop();
        this.exemplarsListener.stop();
    }
}
__decorate([
    observable
], DBListeners.prototype, "isListening", void 0);
//# sourceMappingURL=data:application/json;base64,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